/*
 *    Copyright [2007-2025] [wisemapping]
 *
 *   Licensed under WiseMapping Public License, Version 1.0 (the "License").
 *   It is basically the Apache License, Version 2.0 (the "License") plus the
 *   "powered by wisemapping" text requirement on every single page;
 *   you may not use this file except in compliance with the License.
 *   You may obtain a copy of the license at
 *
 *       https://github.com/wisemapping/wisemapping-open-source/blob/main/LICENSE.md
 *
 *   Unless required by applicable law or agreed to in writing, software
 *   distributed under the License is distributed on an "AS IS" BASIS,
 *   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *   See the License for the specific language governing permissions and
 *   limitations under the License.
 */

import AppConfig from './classes/app-config';
import ReactGA from 'react-ga4';
import { createJsonResponse } from './utils/response';
import JwtTokenConfig from './classes/jwt-token-config';
import { setAnalyticsUserEmail } from './utils/analytics';
import { appLogger as log } from './utils/logger';
import queryClient from './queryClient';
import AppI18n from './classes/app-i18n';
import { preloadMessages } from './classes/app-i18n/messages';

export const loader = async (): Promise<Response> => {
  // Ensure configuration is loaded before continuing, and the user's messages, so that the first
  // paint is in their language.
  await Promise.all([AppConfig.initialize(), preloadMessages(AppI18n.getUserLocale().code)]);

  // Google Analytics Initialization.
  const trackingId = AppConfig.getGoogleAnalyticsAccount();
  if (trackingId) {
    ReactGA.initialize([
      {
        trackingId: trackingId,
      },
    ]);

    // Initialize user ID if user is already logged in. Skip expired tokens: the cookie
    // outlives the JWT, and the request would only 401 and trigger session-expired handling.
    const token = JwtTokenConfig.retreiveToken();
    if (token && !JwtTokenConfig.isTokenExpired(token)) {
      // Fetch account info and set user ID asynchronously
      // Don't await to avoid blocking the loader
      // Through the ['account'] query, which the page reads next: one request, not two.
      const client = AppConfig.getClient();
      queryClient
        .fetchQuery({ queryKey: ['account'], queryFn: () => client.fetchAccountInfo() })
        .then((accountInfo) => {
          setAnalyticsUserEmail(accountInfo.email);
        })
        .catch((error) => {
          // Don't block app load if analytics fails
          log.warn('Failed to set analytics user ID on app load:', error);
        });
    }
  }
  return createJsonResponse('Load success');
};
